import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "./firebase/firebase";

const cardBase = {
  borderRadius: 18,
  padding: 20,
  color: "#fff",
  boxShadow: "0 10px 28px rgba(15,23,42,0.14)",
};

function isCancelled(driver) {
  return ["cancelled", "inactive", "terminated"].includes(
    String(driver?.status || "").trim().toLowerCase()
  );
}

export default function DashboardOperation() {
  const email = localStorage.getItem("email");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const onError = (err) => {
      console.error("Operations dashboard live count error:", err);
      setError("Some live dashboard totals could not be loaded.");
    };

    const unsubDrivers = onSnapshot(
      collection(db, "drivers"),
      (snap) => setDrivers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError
    );

    const unsubVehicles = onSnapshot(
      collection(db, "vehicles"),
      (snap) => setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError
    );

    const unsubAssignments = onSnapshot(
      collection(db, "driverCarAssignments"),
      (snap) =>
        setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError
    );

    return () => {
      unsubDrivers();
      unsubVehicles();
      unsubAssignments();
    };
  }, []);

  const stats = useMemo(() => {
    const activeEmployees = drivers.filter((d) => !isCancelled(d));
    const activeEmployeeIds = new Set(activeEmployees.map((d) => d.id));

    // Current assignments are the source of truth: an open assignment has no endDate.
    const currentAssignments = assignments.filter(
      (a) => !a.endDate && activeEmployeeIds.has(a.driverId)
    );

    const assignedDriverIds = new Set(
      currentAssignments.map((a) => a.driverId).filter(Boolean)
    );
    const assignedVehicleIds = new Set(
      currentAssignments.map((a) => a.vehicleId).filter(Boolean)
    );
    const mainAssignments = currentAssignments.filter(
      (a) => String(a.driverRole || "main").toLowerCase() !== "second"
    );
    const mainVehicleIds = new Set(
      mainAssignments.map((a) => a.vehicleId).filter(Boolean)
    );

    return {
      activeEmployees: activeEmployees.length,
      assignedDrivers: assignedDriverIds.size,
      unassignedDrivers: Math.max(activeEmployees.length - assignedDriverIds.size, 0),
      assignedCars: assignedVehicleIds.size,
      mainAssignedCars: mainVehicleIds.size,
      secondDriverAssignments: currentAssignments.filter(
        (a) => String(a.driverRole || "main").toLowerCase() === "second"
      ).length,
      totalVehicleRecords: vehicles.length,
    };
  }, [drivers, vehicles, assignments]);

  const cards = [
    {
      label: "Active Employees",
      value: stats.activeEmployees,
      note: "Excludes cancelled/inactive employees",
      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
    },
    {
      label: "Currently Assigned Drivers",
      value: stats.assignedDrivers,
      note: "Unique drivers with an open assignment",
      background: "linear-gradient(135deg,#059669,#22c55e)",
    },
    {
      label: "Currently Assigned Cars",
      value: stats.assignedCars,
      note: "Unique cars in open assignments",
      background: "linear-gradient(135deg,#7c3aed,#a855f7)",
    },
    {
      label: "Unassigned Drivers",
      value: stats.unassignedDrivers,
      note: "Active employees without an open assignment",
      background: "linear-gradient(135deg,#ea580c,#f59e0b)",
    },
  ];

  return (
    <div style={{ padding: 30, maxWidth: 1250, margin: "0 auto" }}>
      <div
        style={{
          background: "linear-gradient(135deg,#0f172a,#1e3a8a)",
          color: "white",
          borderRadius: 22,
          padding: "28px 30px",
          boxShadow: "0 14px 35px rgba(15,23,42,0.2)",
        }}
      >
        <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 700 }}>
          OPERATIONS OVERVIEW
        </div>
        <h1 style={{ margin: "6px 0 4px", fontSize: 30 }}>Live Fleet Dashboard</h1>
        <div style={{ opacity: 0.86 }}>Welcome, {email}</div>
        <div style={{ marginTop: 10, fontSize: 13, opacity: 0.75 }}>
          Counts update automatically whenever an employee is assigned, reassigned,
          unassigned, cancelled, or restored.
        </div>
      </div>

      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 10,
            background: "#fef2f2",
            color: "#991b1b",
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gap: 18,
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          marginTop: 22,
        }}
      >
        {cards.map((card) => (
          <div key={card.label} style={{ ...cardBase, background: card.background }}>
            <div style={{ fontSize: 13, opacity: 0.86, fontWeight: 700 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 38, fontWeight: 900, marginTop: 8 }}>
              {card.value}
            </div>
            <div style={{ fontSize: 12, opacity: 0.82, marginTop: 6 }}>
              {card.note}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 22,
          display: "grid",
          gap: 18,
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        }}
      >
        <div style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #e5e7eb" }}>
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 800 }}>MAIN-DRIVER CARS</div>
          <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{stats.mainAssignedCars}</div>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 5 }}>Cars whose current open assignment is marked main driver</div>
        </div>
        <div style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #e5e7eb" }}>
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 800 }}>SECOND-DRIVER ASSIGNMENTS</div>
          <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{stats.secondDriverAssignments}</div>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 5 }}>Open assignments marked as second driver</div>
        </div>
        <div style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #e5e7eb" }}>
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 800 }}>TOTAL VEHICLE RECORDS</div>
          <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{stats.totalVehicleRecords}</div>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 5 }}>All vehicle records, including sold or inactive records</div>
        </div>
      </div>
    </div>
  );
}
